import { useQuery } from "@tanstack/react-query"
import { useParams } from "@tanstack/react-router"
import { medicalsMedicationsPaymentHistoryQueryOptions } from "../api/queries"
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@workspace/web-ui/components/table";

import { Separator } from "@workspace/web-ui/components/separator";
import { formatMedicationDate } from "@workspace/lib/date";

import { Paginator } from "./components/pagination";

import { PaymentHistory } from "~/features/dashboard/pages/medicals/types";

const ITEMS_PER_PAGE = 5; // Display 5 items per page

export const getRefillMedicationStatusConfig = (item: PaymentHistory) => {
  const status = item.status

  const statusMap = {
    "paid": { color: "bg-[#EBFBF1] text-[#14AD4D] hover:bg-[#EBFBF1]", text: "Paid", status: "paid" },
    "failed": { color: "bg-[#FFEBEB] text-[#FF3B3B] hover:bg-[#FFEBEB]", text: "Failed", status: "failed" },
  };

  return statusMap[status as keyof typeof statusMap];
};

// Add skeleton row component
function PaymentHistorySkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="animate-pulse">
          <td colSpan={4} className="py-3">
            <div className="h-4 w-full bg-gray-200 rounded" />
          </td>
        </tr>
      ))}
    </>
  );
}

export function PaymentHistoryPage() {
  const { program } = useParams({ strict: false })
  const paymentHistoryQuery = useQuery(medicalsMedicationsPaymentHistoryQueryOptions({
    program: program === "weight-loss" ? "weight_loss" : "urgent_care",
    limit: 5,
    skip: 0
  }))
  const [currentPage, setCurrentPage] = useState(1);


  // Calculate total pages
  const totalPages = Math.ceil(paymentHistoryQuery.data?.payments?.length || 0 / ITEMS_PER_PAGE);

  // const totalCount = paymentHistoryQuery.data?.payment_history?.length || 0;

  // Get data for the current page
  const paginatedData = paymentHistoryQuery.data?.payments?.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div className="border rounded-xl shadow-sm bg-white w-full mb-4">
      <h3 className="flex items-center text-lg font-medium text-left p-5 text-[#1A202C]">Payment History</h3>
      <Separator />

      {/* Table */}
      <Table className="w-full rounded-lg">
        <TableHeader className="">
          <TableRow className="border-y text-xs min-w-0">
            <TableHead className="text-left text-[#202020CC] whitespace-nowrap">Type</TableHead>
            <TableHead className="text-left text-[#202020CC] px-5 whitespace-nowrap">Date</TableHead>
            <TableHead className="text-left text-[#202020CC] whitespace-nowrap">Amount</TableHead>
            <TableHead className="text-left text-[#202020CC] whitespace-nowrap">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paymentHistoryQuery.isLoading ? (
            <PaymentHistorySkeletonRows rows={ITEMS_PER_PAGE} />
          ) : (
            paginatedData?.map((item, index) => {
              const { color, text } = getRefillMedicationStatusConfig(item)

              return (
                <TableRow key={index} className={`border-0 p-7 text-[#2D2F35] text-sm`}>
                  <TableCell>{item.payment_type}</TableCell>
                  <TableCell>{formatMedicationDate(item.payment_date)}</TableCell>
                  <TableCell>${item.amount.toFixed(2)}</TableCell>
                  <TableCell>
                    <span
                      className={`px-3 py-1 text-xs font-medium rounded-full ${color}`}
                    >
                      {text}
                    </span>
                  </TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>


      <Paginator items={paymentHistoryQuery.data?.payments || []} totalPages={totalPages} currentPage={currentPage} onPageChange={setCurrentPage} itemsPerPage={ITEMS_PER_PAGE} className="border-t" />
    </div>
  )
}
